import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  Zap,
  FileText,
  BookOpen,
  PoundSterling,
  ArrowRight,
  HardHat,
  Clock,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/for-sole-traders";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Estimating Software for Sole Trader Builders",
  "description": "Estimating and quoting software for sole trader builders in the UK. Generate accurate, professional quotes without needing a dedicated estimator. From £29/month.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "For Sole Traders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best estimating software for a sole trader builder in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK sole traders, The Proper Tool offers a purpose-built AI quoting platform at £29/month, less than an hour of your time. It generates a fully itemised quote from drawings, photos, or a plain-text description in under 10 minutes, using a 519-item UK cost library across 31 trade categories. A 14-day free trial is available with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, no credit card required.",
      },
    },
    {
      "@type": "Question",
      "name": "How do sole traders handle VAT on building quotes?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool includes VAT handling built in. Each quote can be toggled to show prices inclusive or exclusive of VAT. The customer-facing PDF reflects your VAT setting, and the internal breakdown always shows net figures. If you are not VAT-registered, simply leave VAT off. The tool does not require it.",
      },
    },
    {
      "@type": "Question",
      "name": "Can sole traders use AI quoting software without technical knowledge?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool is built for builders, not software engineers. You describe the job in plain English, upload drawings or photos if you have them, and the AI produces the itemised quote. You review each line, make adjustments, and export a branded PDF. No training or technical background is needed.",
      },
    },
    {
      "@type": "Question",
      "name": "How does AI help a sole trader win more jobs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Speed and accuracy. A sole trader using The Proper Tool can respond to an enquiry with a professional quote on the same day, while a competitor is still measuring up. Accurate pricing from a 519-item UK cost library means you're not underpricing to win and losing money, or overpricing and losing the job. The built-in validation pipeline also catches missed scope items before you send.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there quoting software designed for sole trader builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool's Lite plan (£29/month) is designed specifically for sole traders. It includes 10 AI auto-quotes per month, unlimited template and manual quotes, professional PDF export, and the full 519-item UK cost library. A 14-day free trial is available with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, no credit card required.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What is the best estimating software for a sole trader builder in the UK?",
    a: "For UK sole traders, The Proper Tool offers a purpose-built AI quoting platform at £29/month, less than an hour of your time. It generates a fully itemised quote from drawings, photos, or a plain-text description in under 10 minutes, using a 519-item UK cost library across 31 trade categories. A 14-day free trial is available with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, no credit card required.",
  },
  {
    q: "How do sole traders handle VAT on building quotes?",
    a: "The Proper Tool includes VAT handling built in. Each quote can be toggled to show prices inclusive or exclusive of VAT. The customer-facing PDF reflects your VAT setting, and the internal breakdown always shows net figures. If you are not VAT-registered, simply leave VAT off. The tool does not require it.",
  },
  {
    q: "Can sole traders use AI quoting software without technical knowledge?",
    a: "Yes. The Proper Tool is built for builders, not software engineers. You describe the job in plain English, upload drawings or photos if you have them, and the AI produces the itemised quote. You review each line, make adjustments, and export a branded PDF. No training or technical background is needed.",
  },
  {
    q: "How does AI help a sole trader win more jobs?",
    a: "Speed and accuracy. A sole trader using The Proper Tool can respond to an enquiry with a professional quote on the same day, while a competitor is still measuring up. Accurate pricing from a 519-item UK cost library means you're not underpricing to win and losing money, or overpricing and losing the job. The built-in validation pipeline also catches missed scope items before you send.",
  },
  {
    q: "Is there quoting software designed for sole trader builders?",
    a: "The Proper Tool's Lite plan (£29/month) is designed specifically for sole traders. It includes 10 AI auto-quotes per month, unlimited template and manual quotes, professional PDF export, and the full 519-item UK cost library. A 14-day free trial is available with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, no credit card required.",
  },
];

const FEATURES = [
  {
    icon: Zap,
    title: "Quote in under 10 minutes",
    description: "Describe the job or upload your drawings. The AI builds a fully itemised scope with UK rates applied. Respond to enquiries the same day, faster than any competitor still using a spreadsheet.",
  },
  {
    icon: FileText,
    title: "Reads structural drawings",
    description: "Upload architect's drawings or structural plans as PDFs. The AI extracts beam sizes, spans, floor areas, and connection details to price the job accurately, without you doing the takeoff.",
  },
  {
    icon: BookOpen,
    title: "All trades, one quote",
    description: "31 trade categories in a single document: groundworks, steelwork, carpentry, plastering, M&E, roofing, and more. You are not limited to your own trade. Price the full scope and subcontract what you don't cover.",
  },
  {
    icon: PoundSterling,
    title: "VAT-ready quotes",
    description: "Toggle VAT on or off per quote. Customer-facing PDFs reflect your VAT setting. Internal breakdowns always show net figures so you know your margin.",
  },
  {
    icon: HardHat,
    title: "Validation before you send",
    description: "A built-in pipeline flags scope gaps and pricing outliers before the quote leaves your hands. Catch missing trades and underpriced items before they cost you money on site.",
  },
  {
    icon: Clock,
    title: "Pricing that learns your rates",
    description: "The Pricing Accuracy Index tracks which quotes you win and self-calibrates toward your accepted margins, so every quote gets closer to your real numbers over time.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "1",
    title: "Describe the job or upload your inputs",
    description: "Type a plain-text description of the project, upload architect's drawings or structural plans, attach site photos, or any combination. Work from whatever you have on site or in the van.",
  },
  {
    step: "2",
    title: "AI generates the itemised scope",
    description: "The AI reads your inputs and produces a fully itemised scope: every trade, every line item, quantities and UK rates drawn from the 519-item cost library. Typically done in under 10 minutes.",
  },
  {
    step: "3",
    title: "Review, adjust, and send",
    description: "Review every line, apply or dismiss validation flags, add or remove items, then export a branded PDF to send to your client, on the same day you got the enquiry.",
  },
];

export default function ForSoleTradersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Estimating Software for Sole Trader Builders UK | The Proper Tool"
        description="Estimating software for sole trader builders in the UK. Generate professional quotes from drawings, photos or a description in under 10 minutes. No estimator needed. From £29/mo, 14-day free trial."
        canonical="/for-sole-traders"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>For Sole Traders</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Estimating software for sole trader builders UK
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              You're not going to hire an estimator. But you can have one. The Proper Tool generates a fully-priced, professional quote from your drawings, photos, or a plain-text description. All in under 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/ai-quoting-software">How Smart Quoting works</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Pain points */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">The sole trader's quoting reality</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              As a sole trader, you are the estimator, the project manager, and the person on the tools. Quoting takes time you don't have, and if you undercharge, there's no one else to absorb the loss.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Winning jobs on price without destroying your margin is the hardest balance in the trade. Price too high and you lose the work. Price too low and you're losing money while you do it. Miss a trade in the scope and you're paying for it yourself.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The Proper Tool generates a fully itemised quote, from groundworks to decoration, priced from real UK rates, in under 10 minutes. You review, adjust to your margin, and send. VAT handled however you need it.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
              <p className="text-lg text-muted-foreground">Three steps from job enquiry to client-ready quote.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-8">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.step} className="relative">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="features">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Built for sole traders, not large teams</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                No complex onboarding. No training. Just a faster, more accurate way to quote.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-background rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-primary mb-2">10 min</p>
                <p className="text-muted-foreground">Average time to generate a full quote from a description or drawings</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">519</p>
                <p className="text-muted-foreground">UK construction rates across 31 trade categories</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-primary mb-2">£29/mo</p>
                <p className="text-muted-foreground">Lite plan: less than one hour of your day-rate</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "Perfect for sole traders", features: ["10 AI auto-quotes/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 519-item cost library", "VAT toggle per quote"], highlight: true },
                { name: "Starter", price: "£59", desc: "For growing sole traders", features: ["25 AI auto-quotes/mo", "Everything in Lite", "Pricing Accuracy Index", "Expert validation pipeline", "Regional pricing"], highlight: false },
                { name: "Pro", price: "£99", desc: "For high-volume traders", features: ["50 AI auto-quotes/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related pages */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/ai-quoting-software", label: "AI Quoting Software for UK Builders" },
                { href: "/for-small-builders", label: "For Small Builders" },
                { href: "/tradesman-quoting-tool", label: "Tradesman Quoting Tool" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/auto-quote", label: "Auto Quote: under 10 minutes" },
                { href: "/pricing", label: "Pricing & Plans" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to quote faster?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your 14-day free trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
